import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import type { GlyphColor } from '../../../types/glyph';

export interface BasicSphereInfo {
  id: string;
  label: string;
  name: string;
  type: string;
  glyph: string;
  glyphColor?: GlyphColor;
  imageId: string;
  is_private?: boolean;
}

export interface UseSphereColumnsResult {
  /** Lookup map of sphere ID to sphere info */
  sphereLookup: Record<string, BasicSphereInfo>;
  /** Whether sphere data is currently loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
}

/**
 * Hook that batch-fetches sphere data for columns marked with `isSphereId: true`.
 *
 * @param data - The data array being displayed
 * @param columns - Column definitions (checks for isSphereId flag)
 * @returns Lookup map of sphere IDs to their basic info
 */
export function useSphereColumns<T = unknown>(
  data: T[],
  columns: DataGridColumn[]
): UseSphereColumnsResult {
  const apiClients = useApiClientsOrNull();
  const [sphereLookup, setSphereLookup] = useState<Record<string, BasicSphereInfo>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find columns that have isSphereId: true
  const sphereColumns = useMemo(() => {
    return columns.filter(col => col.isSphereId === true);
  }, [columns]);

  // Extract unique sphere IDs from data for sphere columns
  const sphereIds = useMemo(() => {
    if (sphereColumns.length === 0 || !data || data.length === 0) {
      return [];
    }

    const ids = new Set<string>();

    for (const row of data) {
      for (const col of sphereColumns) {
        const value = (row as Record<string, unknown>)[col.field];
        if (typeof value === 'string' && value.trim()) {
          ids.add(value);
        }
      }
    }

    return Array.from(ids);
  }, [data, sphereColumns]);

  // Filter out IDs we already have in lookup
  const missingIds = useMemo(() => {
    return sphereIds.filter(id => !sphereLookup[id]);
  }, [sphereIds, sphereLookup]);

  // Batch fetch missing sphere data
  const fetchSpheres = useCallback(async (ids: string[]) => {
    if (!apiClients?.graphClient || ids.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Batch in chunks of 100 (API limit)
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 100) {
        chunks.push(ids.slice(i, i + 100));
      }

      const allResults: Record<string, BasicSphereInfo> = {};

      for (const chunk of chunks) {
        const response = await apiClients.graphClient.post<{
          spheres: Record<string, BasicSphereInfo>;
        }>('/spheres/batch-info', { ids: chunk });

        if (response?.spheres) {
          Object.assign(allResults, response.spheres);
        }
      }

      setSphereLookup(prev => ({
        ...prev,
        ...allResults,
      }));
    } catch (err) {
      // Non-critical error - log but don't break the UI
      const message = err instanceof Error ? err.message : 'Failed to fetch sphere info';
      setError(message);
      console.warn('[useSphereColumns] Failed to fetch sphere info:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiClients?.graphClient]);

  // Fetch when we have missing IDs
  useEffect(() => {
    if (missingIds.length > 0) {
      fetchSpheres(missingIds);
    }
  }, [missingIds, fetchSpheres]);

  return {
    sphereLookup,
    isLoading,
    error,
  };
}

export default useSphereColumns;
